import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Download, Share, PlusSquare } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"

interface InstallAgentButtonProps {
    className?: string
    fullLabel?: boolean
}

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

export function InstallAgentButton({ className, fullLabel = false }: InstallAgentButtonProps) {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
    const [showIOSInstructions, setShowIOSInstructions] = useState(false)
    const [isIOS, setIsIOS] = useState(false)
    const [isStandalone, setIsStandalone] = useState(false)

    useEffect(() => {
        // Check if already installed/standalone
        if (window.matchMedia('(display-mode: standalone)').matches || (window.navigator as Navigator & { standalone?: boolean }).standalone) {
            setIsStandalone(true)
        }

        // Check for iOS
        const userAgent = window.navigator.userAgent.toLowerCase()
        const isIosDevice = /iphone|ipad|ipod/.test(userAgent)
        setIsIOS(isIosDevice)

        // Listen for install prompt (Chrome/Android)
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault()
            setDeferredPrompt(e as BeforeInstallPromptEvent)
        }

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
        }
    }, [])

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt()
            const { outcome } = await deferredPrompt.userChoice
            if (outcome === 'accepted') {
                setDeferredPrompt(null)
            }
        } else if (isIOS) {
            setShowIOSInstructions(true)
        } else {
            // Desktop fallback
            alert('To install this agent, look for the "Install" icon in your browser address bar (usually on the right side).')
        }
    }

    if (isStandalone) return null

    // Only show button if we can prompt OR it's iOS OR we want to show manual instructions
    // if (!deferredPrompt && !isIOS) return null 
    // Allow showing button to provide manual instructions if prompt is not available

    return (
        <>
            <Button
                variant="outline"
                size="sm"
                className={cn(
                    "gap-2 border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white",
                    className
                )}
                onClick={handleInstallClick}
            >
                <Download className="h-4 w-4" />
                {fullLabel ? (
                    <span>Save App</span>
                ) : (
                    <span className="hidden sm:inline">Save App</span>
                )}
            </Button>

            <Dialog open={showIOSInstructions} onOpenChange={setShowIOSInstructions}>
                <DialogContent className="border-slate-800 bg-slate-950 text-slate-100 sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add to Home Screen</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Install this agent as an app on your device for quick access.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col gap-4 py-4">
                        <div className="flex items-center gap-4 rounded-lg border border-slate-800 bg-slate-900 p-4">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800">
                                <Share className="h-5 w-5 text-blue-400" />
                            </div>
                            <div className="text-sm">
                                1. Tap the <span className="font-semibold text-blue-400">Share</span> button in your browser toolbar.
                            </div>
                        </div>
                        <div className="flex items-center gap-4 rounded-lg border border-slate-800 bg-slate-900 p-4">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800">
                                <PlusSquare className="h-5 w-5 text-slate-200" />
                            </div>
                            <div className="text-sm">
                                2. Scroll down and tap <span className="font-semibold text-slate-200">Add to Home Screen</span>.
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )
}
